import { forwardRef } from 'react';
import { css } from '@emotion/react';
import {
  Coverage,
  InsuranceVerificationDetailsResponse,
} from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import { ErrorDetails } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v2/insurance_verification_adapter.pb';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Heading, ModalControlResponse, useModalControl } from '@frontend/design-system';
import { insuranceTabTrackingIds } from '../tracking-ids';
import { DateRangeOptionValue } from '../utils/constants';
import { InsuranceDetails, UndefinedOrNull } from '../utils/types';
import { ActionsMenu } from './actions-menu';
import { ErrorBanner } from './error-banner';
import { InsuranceCardBannerNotification } from './insurance-card-banner-notification';
import { InsuranceDetailList } from './insurance-details-list';
import { InsuranceLoader } from './insurance-loader';
import { InsuranceStatus } from './insurance-status';
import { VerificationMetricSidePanel } from './verification-metric-side-panel';
import { VerifiedInsuranceDetailsList } from './verified-insurance-details-list';
import { VerifyInsuranceSidePanel } from './verify-insurance-side-panel';
import { WritebackSidePanel } from './write-back-side-panel';

interface InsuranceProps {
  defaultDateRange: DateRangeOptionValue;
  isReFetching: boolean;
  locationId: string;
  insuranceDetails: UndefinedOrNull<InsuranceDetails>;
  insuranceVerificationAPIResData: UndefinedOrNull<InsuranceVerificationDetailsResponse>;
  hasErrors: boolean;
  hasCoverage: boolean;
  hasActiveOrVerifiedInsurance: boolean;
  shouldShowWriteBack: boolean;
  requestId?: string;
  isAutoVerification: boolean;
  personDetailsCoverage: UndefinedOrNull<Coverage>;
  isEnhancedInsuranceVerificationEnabled: boolean;
  verificationMetricModalControl: ModalControlResponse;
  errorDetails?: ErrorDetails;
  isIntegratedOffice?: boolean;
  refetchInsuranceDetails?: () => void;
  updateDefaultDateRange: (range: DateRangeOptionValue) => void;
  handleOpenInsuranceReport?: (shouldShowTuuthFairyReport?: boolean) => void;
}
export const Insurance = forwardRef<HTMLElement, InsuranceProps>(
  (
    {
      locationId,
      insuranceDetails,
      requestId,
      hasErrors,
      hasCoverage,
      shouldShowWriteBack,
      hasActiveOrVerifiedInsurance,
      insuranceVerificationAPIResData,
      isAutoVerification,
      personDetailsCoverage,
      defaultDateRange,
      isEnhancedInsuranceVerificationEnabled,
      isReFetching,
      errorDetails,
      verificationMetricModalControl,
      isIntegratedOffice,
      refetchInsuranceDetails,
      updateDefaultDateRange,
      handleOpenInsuranceReport,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const verifyInsuranceModalControl = useModalControl();
    const writeBackModal = useModalControl();
    //sync button and retrieving notification will only be shown in progress state for enhanced verification
    const enhancedVerificationInprogress =
      isEnhancedInsuranceVerificationEnabled && insuranceDetails?.verificationStatus === 'IN_PROGRESS';
    const showVerifyButton = (hasErrors || !hasCoverage) && !enhancedVerificationInprogress;
    const showSyncButton = enhancedVerificationInprogress;
    const shouldShowTuuthFairyReport =
      insuranceVerificationAPIResData?.coverage?.thirdPartyCoverage?.partner === 'TuuthFairy';

    return (
      <>
        <section ref={ref} css={{ padding: theme.spacing(2) }}>
          <header css={{ display: 'flex', justifyContent: 'space-between', marginBottom: theme.spacing(1) }}>
            <div css={headingContainerStyles}>
              <Heading level={3}>{hasActiveOrVerifiedInsurance ? t('Payer Details') : t('Insurance')}</Heading>
              <InsuranceStatus
                enhancedVerificationInprogress={enhancedVerificationInprogress}
                coverageStatus={insuranceDetails?.coverageStatus}
                hasErrors={hasErrors}
                locationId={locationId}
                onVerifyClick={verifyInsuranceModalControl.openModal}
              />
            </div>
            <div css={headingContainerStyles}>
              {showSyncButton && (
                <Button
                  trackingId={insuranceTabTrackingIds.enhancedEligibilityRefreshButton}
                  size='large'
                  iconName='sync'
                  variant='secondary'
                  disabled={isReFetching}
                  onClick={() => refetchInsuranceDetails?.()}
                />
              )}
              <ActionsMenu
                shouldShowTuuthFairyReport={shouldShowTuuthFairyReport}
                verificationStatus={insuranceDetails?.thirdPartyCoverageStatus}
                verifyInsuranceModalControl={verifyInsuranceModalControl}
                onVerificationMetricClick={() => updateDefaultDateRange('today')}
                shouldShowWriteBack={shouldShowWriteBack}
                writeBackModal={writeBackModal}
                requestId={requestId}
              />
            </div>
          </header>
          {/* isRefetching will be true when user hit sync icon */}
          {isReFetching ? (
            <InsuranceLoader />
          ) : (
            <>
              {!insuranceDetails?.shouldShowOverriddenStatus && (
                <>
                  <ErrorBanner
                    isRetryButtonVisible //should be person data
                    errorDetails={personDetailsCoverage?.thirdPartyCoverage?.errorDetails || errorDetails}
                    onRetryClick={verifyInsuranceModalControl.openModal}
                    isAutoVerification={isAutoVerification}
                  />

                  <InsuranceCardBannerNotification
                    isEligibleForWebsiteResp={!!insuranceDetails?.isElegibleForWebsiteResp}
                    enhancedVerificationInprogress={enhancedVerificationInprogress}
                  />
                </>
              )}
              {hasActiveOrVerifiedInsurance ? (
                <>
                  <VerifiedInsuranceDetailsList
                    insuranceVerificationAPIResData={insuranceVerificationAPIResData}
                    locationId={locationId || ''}
                    pmsPayerId={insuranceDetails?.pmsPayerId || ''}
                    isIntegratedOffice={isIntegratedOffice}
                  />
                  <Button
                    trackingId={insuranceTabTrackingIds.viewReportButton}
                    variant='tertiary'
                    css={css({
                      width: '100%',
                      marginTop: theme.spacing(1),
                    })}
                    onClick={() => handleOpenInsuranceReport && handleOpenInsuranceReport(shouldShowTuuthFairyReport)}
                  >
                    {t('View Report')}
                  </Button>
                </>
              ) : (
                <>
                  <InsuranceDetailList
                    isIntegratedOffice={isIntegratedOffice}
                    insuranceDetails={insuranceDetails as InsuranceDetails}
                  />
                  {showVerifyButton && (
                    <Button
                      trackingId={insuranceTabTrackingIds.verifyManuallyButton}
                      variant='tertiary'
                      css={css({
                        width: '100%',
                        marginTop: theme.spacing(1),
                      })}
                      onClick={verifyInsuranceModalControl.openModal}
                    >
                      {t('Verify Manually')}
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </section>
        <WritebackSidePanel {...writeBackModal.modalProps} />
        <VerifyInsuranceSidePanel
          {...verifyInsuranceModalControl.modalProps}
          hasVerificationError={!!hasErrors}
          shouldShowTuuthFairyReport={shouldShowTuuthFairyReport}
          onReportLearnMoreClick={() => updateDefaultDateRange('this-month')}
        />
        <VerificationMetricSidePanel {...verificationMetricModalControl.modalProps} dateRange={defaultDateRange} />
      </>
    );
  }
);
Insurance.displayName = 'Insurance';

const headingContainerStyles = css({ display: 'flex', alignItems: 'center', gap: theme.spacing(1) });
